<template>
  <OutsorsingBanner />
  <section-layout name="outsourcing-article" margin="true">
    <p>
      It is natural to still believe that the development work is always
      something which is better to manage in-house, because when you talk to the
      developers personally, you feel that you have more control over the work
      they do.
    </p>
    <p>
      Nowadays; however, that feeling of control is nothing more than an
      illusion. The reason is that since the pandemic, nearly 90% of all staff
      work remotely; and even after a successful global vaccination roll-out, it
      would still take years to return to the normal office working environment
      to which we were previously accustomed.
    </p>
    <p>
      Consequently, since most employees are currently still working remotely,
      the difference between outsourcing and in-staff developers is minimal and
      it is therefore more reasonable to weigh the pros and cons between
      employing and training an individual (or a team), versus hiring an agency
      to streamline the entire process right from the start.
    </p>
    <p>
      <strong>Are there really any benefits to dealing with an agency?</strong
      ><br />Let’s analyze this:
    </p>
  </section-layout>
  <section-layout
    name="outsourcing-article"
    title="Recruiting costs"
    margin="true"
  >
    <div class="icon-check">
      <img
          v-svg-inline
          :data-src="require('@/assets/icons/check-icon.svg')"
          alt="check icon"
          class="icon-check-svg"
      />
    </div>
    <p>
      Usually, agencies charge between 150% to 250% of the monthly fee
      (salaries), for each successful candidate. Of course, the HR agencies
      promise replacements, but have you ever tried to get them to follow
      through with their promises? Another thing to take into consideration is
      that the recruiting fee would not be waived, even if you hired staff
      yourself.
    </p>
    <p>
      There’s no need to pay recruitment fees when joining us - that’s the
      biggest difference between hiring us and having to recruit an in-house
      developer.
    </p>
    <p>
      We automatically reduce your recruiting expenses and we provide
      multi-tiered options to cover your ongoing needs - regardless of whether
      you’re a fledgling startup or a thriving enterprise on a mission to
      dominate in your industry.
    </p>
  </section-layout>
  <section-layout
    name="outsourcing-article"
    title="Narrow-minded focus of an individual"
    margin="true"
  >
    <div class="icon-check">
      <img
          v-svg-inline
          :data-src="require('@/assets/icons/check-icon.svg')"
          class="icon-check-svg"
          alt="check icon"
      />
    </div>
    <p>
      This is not usually very cost effective and is negated when you are with
      an experienced outsourcing development company which has a professional
      team with a vast collective pool of knowledge in their industry.
    </p>
    <p>
      There was a time when everything was simpler in terms of development.
      There were web developers who covered everything related to websites -
      there were mobile developers and there were desktop developers and most of
      the time, a single web developer could cover all your web development
      needs, but these days there are so many new and constantly changing
      technological trends, that you need to sometimes consider various people
      to cover a single position.
    </p>
    <div class="example">
      <strong class="exmple-title">Example</strong>
      <p>
        Let’s assume that you need to develop or maintain an e-commerce site.
        You may definitely start developing it from scratch, but it’s an
        expensive and very time consuming journey, therefore it’s reasonable to
        select a single, trustworthy platform you’d like to use to alleviate all
        the logistics you’re likely to encounter. So who to hire?
      </p>
      <p>
        There are so many platforms out there, but which one is right for you?
        It’s a major decision to decide on who you hire to analyze data and make
        suggestions, as well as taking expenses for specialist consultants into
        consideration too - these are all very important deciding factors, but
        finally (just as an example), you decide to go with Magento.
      </p>
      <p>
        <em
          >So let’s fast forward just a bit… Magento is quite a complicated
          system. If you think you only need a developer to manage all of your
          Magento tasks, you are sadly mistaken. Sooner or later, you will
          realize that your new Magento employee is not able to handle all the
          tasks alone. The list of tasks the Magento developer will have to face
          is daunting; to say the least, but for most people… this is an almost
          impossible workload:</em
        >
      </p>
      <ul>
        <li>Magento backend</li>
        <li>
          Magento frontend (a universal developer could cover both back and
          frontend, but such a person is more expensive and even he would
          require the help of an HTML coder)
        </li>
        <li>
          Server settings (your host can handle it, but he may charge you
          substantially more for DevOps services)
        </li>
        <li>
          Designer (a developer is not a designer, so you will possibly need to
          hire a designer for tweaking some of the design elements)
        </li>
        <li>
          Tester (you may do everything by yourself but regression testing is
          quite a time-consuming process)
        </li>
        <li>
          If you manage a significant part of the development from scratch, you
          need to involve a software development architect. He will be the most
          expensive to hire, but obviously, you won't need to have him for
          ongoing work.
        </li>
        <li>
          One specification is better than a thousand words, therefore it's
          reasonable to use business analytics to prepare the specs.
        </li>
      </ul>
      <p>
        <em>
          As you can see from the examples above, to develop; as well as to
          support a Magento site, you’d need to have at least the following
          employees at your disposal... as well as on your payroll:
        </em>
      </p>

      <ul>
        <li>Business analyst</li>
        <li>Software development architect</li>
        <li>QA person</li>
        <li>Designer</li>
        <li>Markup guy</li>
        <li>Magento developer</li>
        <li>Project Manager to organize everyone else</li>
      </ul>
    </div>
    <p>
      As is obvious from the list above, the reasonable price for a single
      developer that you may have considered initially is turning into the price
      of a dedicated team that you would need to hire to keep everything running
      smoothly, and even though it would seem reasonable to use part-time
      employees - it would make hiring your dream-team even more difficult.
    </p>
    <p>
      If you trust us with all of the above - you don't ever need to face any of
      these challenges and when it's time for a specific specialist, we provide
      them without any extra hidden charges. If you consider paying for 160
      hours of individual labor a month; then by hiring us, you will be hiring
      at least seven specialists, (see above) for the price of one!
    </p>
    <div class="article-image">
      <img
        v-webp:src="require('@/assets/outsourcing/outsourcing-image-01.webp')"
        :src="require('@/assets/outsourcing/outsourcing-image-01.jpg')"
        alt="Outsourcing section image"
        loading="lazy"
        width="1147"
        height="709"
      />
    </div>
  </section-layout>
  <section-layout
    name="outsourcing-article"
    title="Full scope of services"
    margin="true"
  >
    <div class="icon-check">
      <img
          v-svg-inline
          :data-src="require('@/assets/icons/check-icon.svg')"
          alt="check icon"
          class="icon-check-svg"
      />
    </div>
    <p>
      We provide a full range of services, and if the developer requires the
      assistance of a DevOp, we just involve them as necessary.
    </p>
    <div class="columns">
      <div class="column">
        <ul>
          <li>
            <strong>Community:</strong> No matter how good a developer is, there
            are always tasks that he has not dealt with before and therefore he
            would need to spend more time investigating the problem areas - time
            which could cost you more. Our Magento devs are accustomed to
            working together and they value their own time, so if they face
            something unusual, the first thing they do is share the challenge
            with our vastly experienced team of Magento devs. There is always
            someone more experienced in varied areas; so by hiring one of our
            developers, you are gaining access to the expertise of all our
            Magento devs at absolutely no extra cost!
          </li>
          <li>
            <strong>Tapping into an entire team:</strong> We understand the
            complexities of building a trustworthy, competent team and that
            synchronization is difficult to achieve, so one of the biggest keys
            to our success revolves around the value and respect we have for
            each other. We’ve all worked on various projects together, which
            translates into a beneficially ready-to-go solution for your
            company.
          </li>
          <li>
            <strong>Infrastructure:</strong> By partnering with us, you don't
            need to pay for equipment, software licenses, etc. We take care of
            the infrastructure for all of our projects - you only pay for the
            development time!
          </li>
          <li>
            <strong>Reduction of overhead costs:</strong> no equipment costs or
            software licenses, or even healthcare to cover.
          </li>
        </ul>
      </div>
      <div class="column">
        <ul>
          <li>
            <strong
              >Flexibility in working schedules and access to resources:</strong
            >
            If you hire a full-time developer, you have to pay him monthly… even
            if you don't have tasks for him (e.g. A/B testing is in process and
            you need to hold one development). By partnering with us, you may:
            <ul>
              <li>
                Pause the work for as long as you like and you don’t need to pay
                for the downtime
              </li>
              <li>
                Reduce the loading of the developers to a few hours a day/week
              </li>
              <li>
                Temporarily involve more resources if you need to have a
                particular task completed faster
              </li>
            </ul>
          </li>
          <li>
            <strong>Human-related factors:</strong> Any developer may become
            sick, depressed, or even retire. Unfortunately, you will then need
            to repeat the recruiting/onboarding process and pay for most of the
            initial work once again. We absorb all such risks so that you don’t
            have to.
          </li>
          <li>
            <strong>Motivation of the developers:</strong> The staff needs to be
            motivated and developers are some of the most spoiled employees
            because of their high demands. It would be difficult for you to keep
            your team stable unless you had a strong team of 10+ devs by your
            side. You won’t have that problem with us since we have a big and
            experienced team and we take care of them, thereby keeping the teams
            motivated at all times.
          </li>
          <li>
            <strong>The time differences are sometimes beneficial.</strong> The
            time difference doesn't always play a role, but when we talk about a
            Magento site going live, it means that the site may be down for an
            hour or so during the deployment process, which means it's
            reasonable to usually try to manage deployment during the night -
            but that won’t be a problem when you partner with us.
          </li>
        </ul>
      </div>
    </div>
  </section-layout>
  <section-layout name="outsourcing-article" title="Price" margin="true">
    <div class="icon-check">
      <img
          v-svg-inline
          :data-src="require('@/assets/icons/check-icon.svg')"
          alt="check icon"
          class="icon-check-svg"
      />
    </div>
    <p>
      It's an obvious benefit of outsourcing. The minimum rate of a mid-level
      developer in the US is about $100/h. Our senior-level developer rates run
      from $40/h.
    </p>
    <div class="columns">
      <div class="column">
        <ul>
          <li>
            <strong>Transparent development process.</strong> We provide a
            transparent development process with step by step progress reports
            and a simple system to manage time spent, as well as budget.
          </li>
          <li>
            <strong>Dev servers for free.</strong> During the development stage,
            we provide our own servers for our developers to further develop and
            test. You will not need to additionally pay a hosting company.
          </li>
        </ul>
      </div>
      <div class="column">
        <ul>
          <li>
            <strong>Vast knowledge base and years of experience.</strong> We
            have a vast collective pool of experience amongst ourselves because
            we have always had different types of clients: from huge companies
            to individual clients, we have implemented hundreds of third-party
            services, modules, payments, etc. Our combined knowledge and
            expertise will always be greater than one person hired in-house.
          </li>
        </ul>
      </div>
    </div>
  </section-layout>
  <section-layout name="outsourcing-article" title="Legalities" margin="true">
    <p>
      It’s reasonable that you would want to protect yourself by placing all
      nuances of your collaboration with an individual into a contract.
    </p>
    <div class="columns">
      <div class="column">
        <div class="icon-check">
          <img
            v-svg-inline
            :data-src="require('@/assets/icons/close-icon.svg')"
            alt="close icon"
            class="icon-check-svg"
          />
        </div>
        <p>
          Unfortunately you can’t sign a contract directly with our developers
        </p>
      </div>
      <div class="column">
        <div class="icon-check">
          <img
            v-svg-inline
            :data-src="require('@/assets/icons/check-icon.svg')"
            alt="check icon"
            class="icon-check-svg"
          />
        </div>
        <p>
          But you are more than welcome to outline all nuances of your
          collaboration and to sign a contract with Wdevs Inc - the US company
          (if you are from the US, Canada or Australia)
        </p>
      </div>
    </div>
    <p class="mt">
      Or with Webdevs LTD - the Cyprus company (if you are in the EU)
    </p>
  </section-layout>
  <section-layout
    name="outsourcing-article conclusion"
    title="In conclusion"
    margin="true"
  >
    <p>
      <strong
        >One of the greatest advantages of outsourcing is that hiring a
        competent dev from a 3rd party means that you won’t need to worry about
        micromanaging yet another in-house staff member, and since 90% of local
        developers prefer to work remotely due to the pandemic these days -
        wouldn’t you prefer to know right from the start, that your remote dev
        is competent, being managed on your behalf, has the support of
        specialists in all IT spheres and is getting the job done without any of
        the additional in-house issues usually experienced?</strong
      >
    </p>
    <span class="note"
      >{{text}}
    </span>
    <ContactsButton size="large" :desc="text" :id="btnModalId" />
  </section-layout>
</template>
<script>
import SectionLayout from "@/layouts/SectionLayout";
import OutsorsingBanner from "@/components/OutsorsingBanner.vue";
import ContactsButton from "@/components/ContactsButton.vue";

export default {
  data(){
    return{
      text:'Please don’t hesitate to let us know if you’re still inquisitive, since discussing a step as significant as hiring a dev who you can entrust with the success of your business, is a major consideration!',
      btnModalId: 'outsourcing-section-modal',
    }
  },
  components: {
    SectionLayout,
    OutsorsingBanner,
    ContactsButton,
  },
  created(){
    window.history.scrollRestoration = "auto";
  }
};
</script>

<style lang="scss">
@import "@/scss/_variables.scss";
@import "include-media/dist/_include-media.scss";
.outsourcing-article {
  margin-top: 60px;
  @include media(">=tablet") {
    margin-top: 120px;
  }

  p {
    margin-bottom: 32px;
    &:last-child {
      margin-bottom: 0;
    }
    &.mt {
      margin-top: 30px;
    }
  }
  .section_content {
    display: block;
  }
  .icon-check {
    display: block;
    margin-bottom: 40px;
    &-svg {
      display: block;
      width: 48px;
      height: 48px;
    }
  }
  .article-image {
    margin-top: 40px;
  }
  .example {
    padding: 40px;
    background-color: $gray;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 40px;
    margin-top: 40px;
    .exmple-title {
      display: block;
      margin-bottom: 20px;
    }
    p {
      margin-bottom: 24px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    ul {
      margin: 0;
      padding: 4px 0 0 25px;
      &:last-child {
        li {
          margin-bottom: 10px;
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
      li {
        margin-bottom: 10px;
          &:last-child {
            margin-bottom: 24px;
          }
      }
    }
  }
  .columns {
    margin-top: 40px;
    @include media(">=desktop") {
      display: flex;
    }
    .column {
      margin-bottom: 32px;
      @include media(">=desktop") {
        width: calc(50% - 40px);
        margin-bottom: 0;
        &:first-child {
          margin-right: 80px;
        }
      }
      & > ul {
        margin: 0;
        padding-left: 30px;
        li {
          margin-bottom: 32px;
          &:last-child {
            margin-bottom: 0;
          }
        }
        ul {
          padding-top: 32px;
          padding-left: 20px;
          list-style-type: lower-latin;
          li {
            margin-bottom: 0;
          }
        }
      }
    }
  }
  &.conclusion {
    p {
      font-size: 24px;
      line-height: 1.7;
      font-weight: 700;
      margin-bottom: 40px;
    }
    .note {
      display: block;
      margin-top: 120px;
      margin-bottom: 40px;
      font-style: italic;
    }
  }
}
</style>
